import {
  ProductCard as UIProductCard,
  ProductCardContent as UIProductCardContent,
  ProductCardImage as UIProductCardImage,
} from '@faststore/ui'
import type { ProductSummary_ProductFragment } from '@generated/graphql'
import { graphql } from 'gatsby'
import type {
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
} from 'react'
import { memo, useCallback, useMemo, useState } from 'react'
import { useCmsCollectionFlags } from 'src/components/hooks/useCmsGlobalFlags'
import { useGetMulticurrencyPrices } from 'src/components/hooks/useGetMulticurrencyPrices'
import { Badge, DiscountBadge } from 'src/components/ui/Badge'
import BuyButton from 'src/components/ui/BuyButton'
import { ShopIcon } from 'src/components/ui/Icons/ShopIcon'
import { Image } from 'src/components/ui/Image'
import Link from 'src/components/ui/Link'
import Price from 'src/components/ui/Price'
import { QuantitySelector } from 'src/components/ui/QuantitySelector/QuantitySelector'
import { cartStore, useCart } from 'src/sdk/cart'
import { useCartItemEvent } from 'src/sdk/cart/useCartItemEvent'
import { useDiscountPercent } from 'src/sdk/product/useDiscountPercent'
import { useFormattedPrice } from 'src/sdk/product/useFormattedPrice'
import { useProductLink } from 'src/sdk/product/useProductLink'
import { filterAvailableVariations } from 'src/utils/filterAvailableProducts'
import { usePricePerKg } from 'src/utils/usePricePerKg'

import { PRICE_TYPES } from '../../../constants'
import isProductMKT from '../../../utils/productMKTChecker'
import { CollectionFlags } from '../../sections/ProductDetails/CollectionFlags'
import { ProductMKTPrice } from '../../ui/Price/productMKTPrice'
import styles from './product-card.module.scss'

export interface ProductCardProps {
  product: ProductSummary_ProductFragment
  ButtonBuy?: ReactNode
  index?: number
  loading?: 'eager' | 'lazy'
  preload?: boolean
}

type ItemValues = {
  id: string
  quantity: number
  price: number
  listPrice: number
  seller: { identifier: string }
  itemOffered: any
}

type HandleBuyButtonProps = {
  hasVariations?: boolean
  itemValues: ItemValues
  sendCartItemEvent: (item: any, quantity: number) => void
  setQuantityItems: Dispatch<SetStateAction<number>>
  addItem: (item: any) => void
}

const ShowBuyButton = ({
  hasVariations,
  linkProps,
  children,
}: PropsWithChildren<TBuyButton>) => (
  <>{hasVariations ? <Link {...linkProps}>{children}</Link> : children}</>
)

const handleSubmitItem = ({
  itemValues,
  sendCartItemEvent,
  setQuantityItems,
  addItem,
}: HandleBuyButtonProps) => {
  const quantityDelta = 2

  window.InsiderQueue = window.InsiderQueue || []
  window.InsiderQueue.push({
    type: 'add_to_cart',
    value: {
      id: itemValues.id,
      name: itemValues.itemOffered.name,
      unit_sale_price: itemValues.listPrice,
      unit_price: itemValues.price,
      quantity: itemValues.quantity,
      url: window.location.href,
      product_image_url: itemValues.itemOffered.image?.[0]?.url || '',
      custom: {
        merchandiser: itemValues.seller.identifier,
      },
    },
  })

  sendCartItemEvent(itemValues, quantityDelta)
  setQuantityItems(1)
  addItem(itemValues)
}

const handleBuyButton = (handleBuyButtonProps: HandleBuyButtonProps): void => {
  const { hasVariations } = handleBuyButtonProps

  !hasVariations && handleSubmitItem(handleBuyButtonProps)
}

function useSelectedOffer(offer: any, spotPrice: number): number {
  return useMemo(() => {
    const lowestPriceOffer = offer?.findIndex((x: any) => x.price === spotPrice)

    if (lowestPriceOffer === -1) {
      console.error(
        'Could not find the lowest price product offer. Showing the first offer provided'
      )

      return 0
    }

    return lowestPriceOffer
  }, [spotPrice, offer])
}

function ProductCard({
  product,
  ButtonBuy,
  loading = 'eager',
}: ProductCardProps) {
  const {
    id,
    slug,
    sku,
    clusterHighlights,
    brand,
    unitMultiplier,
    measurementUnit,
    isVariantOf: { skuVariants, name, hasVariant },
    image: [img],
    offers: { lowPrice: productSpotPrice, offers: offer },
  } = product
  
  const [availableVariation] = filterAvailableVariations(hasVariant)

  const {
    offers: variationOffers,
    image: [variationImage],
  } = availableVariation

  const { items } = useCart()
  const { addItem, removeItem, updateItemQuantity } = cartStore
  const [quantityItems, setQuantityItems] = useState(0)

  const variationsTalle = skuVariants?.availableVariations?.Talle
  const hasVariations = variationsTalle && variationsTalle.length > 0

  const cartProduct = items?.find(
    (item) => item.itemOffered.sku === product.sku
  )

  const selectedOffer = useSelectedOffer(offer, productSpotPrice)

  const {
    listPrice: productListPrice,
    seller,
    itemOffered,
  } = offer[selectedOffer]

  const { sendCartItemEvent } = useCartItemEvent()

  const onQuantityChange = useCallback(
    (quantity: number, type?: 'add_to_cart' | 'remove_from_cart') => {
      if(!cartProduct) return;
      window.InsiderQueue = window.InsiderQueue || []
      window.InsiderQueue.push({
        type: type ||( quantity > 0 ? 'add_to_cart' : 'remove_from_cart'),
        value: {
          id: itemValues.id,
          name: itemValues.itemOffered.name,
          unit_price: itemValues.price,
          unit_sale_price: itemValues.listPrice,
          quantity: itemValues.quantity,
          url: window.location.href,
          product_image_url: itemValues.itemOffered.image?.[0]?.url || '',
          custom: {
            merchandiser: itemValues.seller.identifier,
          },
        },
      })
      cartProduct && sendCartItemEvent(cartProduct, quantity)
    },
    [cartProduct, sendCartItemEvent]
  )

  const outOfStock = offer?.[0]?.availability !== 'https://schema.org/InStock'

  const useVariant = outOfStock && availableVariation

  const linkProps = useProductLink({
    slug: useVariant ? availableVariation.slug : slug,
    product,
    selectedOffer: 0,
    index: 0,
  })

  const listPrice = useVariant
    ? variationOffers.offers[0].listPrice
    : productListPrice

  const spotPrice = useVariant ? variationOffers.lowPrice : productSpotPrice

  const buyButtonText = hasVariations ? 'COMPRAR' : 'Agregar'

  const realPrice =
    unitMultiplier && unitMultiplier > 1 && unitMultiplier < 2
      ? spotPrice * unitMultiplier
      : spotPrice

  const isProductPerKg = measurementUnit === 'kg'

  const productPerKgOffers = usePricePerKg({
    listPrice,
    unitMultiplier: unitMultiplier ?? 1,
    lowPrice: realPrice,
  })

  const discountPercentage = useDiscountPercent(listPrice, spotPrice)

  const discountValue = isProductPerKg
    ? productPerKgOffers.discountPercentage
    : discountPercentage

  const originalPrice = isProductPerKg
    ? productPerKgOffers.originalProductPrice
    : listPrice

  const actualPrice = isProductPerKg
    ? productPerKgOffers.actualProductPrice
    : spotPrice

  const itemValues = {
    id,
    quantity: 1,
    price: spotPrice,
    listPrice,
    seller,
    itemOffered,
  }

  const handleBuyButtonProps = {
    hasVariations,
    itemValues,
    sendCartItemEvent,
    setQuantityItems,
    addItem,
  }

  const allCmsFlags = useCmsCollectionFlags()

  const sellerIdentifier = offer[0]?.seller.identifier
  const sellerDefault = '1'

  const {
    loading: multicurrencyLoading,
    multicurrecySellingPrice,
    multicurrencyListPrice,
  } = useGetMulticurrencyPrices(sellerIdentifier, sku)

  const memoizedPrices = useMemo(() => {
    return {
      multicurrecySellingPrice,
      multicurrencyListPrice,
      multicurrencyLoading,
    }
  }, [multicurrecySellingPrice, multicurrencyListPrice, multicurrencyLoading])


  return (
    <UIProductCard
      data-fs-product-card
      data-fs-product-card-actionable={!!ButtonBuy}
      data-fs-product-card-sku={sku}
      className={`${styles.fsProductCard}`}
    >
      <div data-fs-product-image-content>
        <UIProductCardImage>
          <CollectionFlags
            clusterHighlights={clusterHighlights}
            allCmsFlags={allCmsFlags}
          />
          <Link {...linkProps} title={name} aria-label={`Link to ${name}`}>
            <Image
              src={useVariant ? variationImage.url : img.url}
              alt={img.alternateName}
              width={0}
              height={0}
              objectFit="contain"
              sizes="(max-width: 768px) 25vw, 30vw"
              loading={loading}
            />
          </Link>
        </UIProductCardImage>
      </div>
      <UIProductCardContent data-fs-product-card-content>
        <div data-fs-product-card-heading>
          <h3 data-fs-product-card-title>
            <Link {...linkProps} title={name} aria-label={`Link to ${name}`}>
              {name}
            </Link>
          </h3>
          <div data-fs-product-card-brand className="product__shelf__brand">
            <p className="product-shelf__brand-text">{brand?.name}</p>
            {sellerIdentifier !== sellerDefault && <ShopIcon />}
          </div>
          <div data-fs-product-card-prices className="product-shelf__prices">
            {(discountValue && !isProductMKT(sellerIdentifier)) ||
            (discountValue &&
              isProductMKT(sellerIdentifier) &&
              memoizedPrices.multicurrecySellingPrice <= 0) ? (
              <Price
                value={originalPrice}
                formatter={useFormattedPrice}
                testId="list-price"
                data-value={listPrice}
                variant="listing"
                classes="text__legend"
                SRText="Original price:"
              />
            ) : (
              <></>
            )}
            {!memoizedPrices.multicurrencyLoading &&
            memoizedPrices.multicurrencyListPrice &&
            isProductMKT(sellerIdentifier) ? (
              <div>
                <ProductMKTPrice
                  priceValue={memoizedPrices.multicurrencyListPrice}
                  priceType={PRICE_TYPES.listPrice}
                  componentToRender="shelfProductCard"
                />
              </div>
            ) : (
              <></>
            )}
            {!isProductMKT(sellerIdentifier) ||
            (isProductMKT(sellerIdentifier) &&
              memoizedPrices.multicurrecySellingPrice <= 0) ? (
              <Price
                value={actualPrice}
                formatter={useFormattedPrice}
                testId="price"
                data-value={actualPrice}
                variant="spot"
                classes="text__body"
                SRText="Sale Price:"
              />
            ) : null}
            {!memoizedPrices.multicurrencyLoading &&
            isProductMKT(sellerIdentifier) &&
            memoizedPrices.multicurrecySellingPrice &&
            memoizedPrices.multicurrecySellingPrice > 0 ? (
              <ProductMKTPrice
                priceValue={
                  typeof memoizedPrices.multicurrecySellingPrice === 'number'
                    ? memoizedPrices.multicurrecySellingPrice
                    : actualPrice
                }
                priceType={PRICE_TYPES.sellingPrice}
                componentToRender="shelfProductCard"
              />
            ) : null}
          </div>
          {isProductPerKg && !isProductMKT(sellerIdentifier) && (
            <div data-fs-product-card-price-unit>
              <p data-fs-product-card-price-unit>&#40;</p>
              <Price
                value={productPerKgOffers.actualKgPrice}
                formatter={useFormattedPrice}
                classes="text_price_unit_card"
                SRText="Sale Price:"
              />
              <p className="text_price_unit_card">x {measurementUnit}</p>
              <p className="text_price_unit_card">&#41;</p>
            </div>
          )}
        </div>

        {outOfStock ? !availableVariation && <Badge>Out of stock</Badge> : null}

        {discountPercentage ? (
          <DiscountBadge discountPercent={discountValue} />
        ) : null}

        {!cartProduct?.id ? (
          <div data-fs-button-container className="buy-button__container">
            <ShowBuyButton hasVariations={hasVariations} linkProps={linkProps}>
              <BuyButton
                className="buy-button"
                onClick={() => handleBuyButton(handleBuyButtonProps)}
                hasIcon={!hasVariations}
              >
                {buyButtonText}
              </BuyButton>
            </ShowBuyButton>
          </div>
        ) : (
          <div
            data-fs-button-container
            className="quantity-selector__container"
          >
            <span>Agregado al carrito</span>
            <QuantitySelector
              unitMultiplier={unitMultiplier ?? 1}
              isProductPerKg={isProductPerKg}
              cartQuantity={cartProduct?.quantity}
              quantity={quantityItems}
              setQuantity={setQuantityItems}
              updateItems={updateItemQuantity}
              removeItem={removeItem}
              productId={cartProduct?.id}
              onChange={onQuantityChange}
            />
          </div>
        )}
      </UIProductCardContent>
    </UIProductCard>
  )
}

export const fragment = graphql`
  fragment ProductSummary_product on StoreProduct {
    id: productID
    slug
    sku
    clusterHighlights {
      id
      name
    }
    brand {
      brandName: name
    }
    name
    gtin
    unitMultiplier
    measurementUnit
    isVariantOf {
      productGroupID
      name
      skuVariants {
        activeVariations
        slugsMap(dominantVariantName: "Talle")
        availableVariations(dominantVariantName: "Talle")
      }
      hasVariant {
        ...ProductSummary_variant
      }
    }
    image {
      url
      alternateName
    }
    brand {
      name
    }
    offers {
      lowPrice
      offers {
        availability
        price
        listPrice
        quantity
        seller {
          identifier
        }
        itemOffered {
          sku
          name
          gtin
          image {
            url
            alternateName
          }
          brand {
            name
          }
          isVariantOf {
            productGroupID
            name
          }
          additionalProperty {
            propertyID
            name
            value
            valueReference
          }
        }
      }
    }
  }

  fragment ProductSummary_variant on StoreProduct {
    slug
    offers {
      lowPrice
      offers {
        availability
        price
        listPrice
      }
    }
    image {
      url
      alternateName
    }
  }
`

export default memo(ProductCard)
